import * as yup from 'yup'
import moment from 'moment'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
// import { AddressSchema } from '~/features/care-activation/utils/validate-form-data'

const MIN_PASSWORD_LENGTH = 8

const useYupValidation = () => {
	const { t } = useTranslation()

	const SIGNIN_EMAIL_VALIDATION_SCHEMA = yup.string().trim().required(t('$ERRORS.FIELD_REQUIRED'))

	const SIGNIN_PASSWORD_VALIDATION_SCHEMA = yup
		.string()
		.required(t('$ERRORS.FIELD_REQUIRED'))
		.min(8, () => t('$ERRORS.PASSWORD_MIN_LENGTH'))

	const PASSWORD_VALIDATION_SCHEMA = yup
		.string()
		.required(t('$ERRORS.FIELD_REQUIRED'))
		.min(8, t('$ERRORS.INVALID_PASSWORD'))
		.matches(/^\S*$/, t('$ERRORS.INVALID_PASSWORD'))
		.matches(/[A-Za-z]/, t('$ERRORS.INVALID_PASSWORD'))
		.matches(/[0-9]/, t('$ERRORS.INVALID_PASSWORD'))

	const PASSWORD_CONFIRMATION_VALIDATION_SCHEMA = yup
		.string()
		.required(t('$ERRORS.FIELD_REQUIRED'))
		.min(MIN_PASSWORD_LENGTH, () => t('$ERRORS.INVALID_PASSWORD'))
		.oneOf([yup.ref('password'), null], "Password doesn't match, please recheck")

	const NEW_PASSWORD_VALIDATION_SCHEMA = yup
		.string()
		.required(t('$ERRORS.FIELD_REQUIRED'))
		.min(8, t('$ERRORS.INVALID_PASSWORD'))
		.matches(/^\S*$/, t('$ERRORS.INVALID_PASSWORD'))
		.matches(/[A-Za-z]/, t('$ERRORS.INVALID_PASSWORD'))
		.matches(/[0-9]/, t('$ERRORS.INVALID_PASSWORD'))

	const NEW_PASSWORD_CONFIRMATION_VALIDATION_SCHEMA = yup
		.string()
		.required(t('$ERRORS.FIELD_REQUIRED'))
		.oneOf([yup.ref('password'), null], "Password doesn't match, please recheck")

	// Resume Parse Schemas
	const CANDIDATE_SCHEMA = yup.object().shape({
		address1: yup
			.object()
			.shape({
				formatted_address: yup.string().trim().required(t('$ERRORS.FIELD_REQUIRED')),
			})
			.required(t('$ERRORS.FIELD_REQUIRED')),
		suiteAptFloor: yup.string(),
		city: yup.string(),
		state: yup.string(),
		zipcode: yup.string(),
		country: yup.string().trim().required(t('$ERRORS.FIELD_REQUIRED')),
		resume: yup.string(),
	})

	const EDUCATION_ITEM_SCHEMA = yup.object().shape({
		school: yup.string(),
		degreeLevel: yup.string(),
		areaOfStudy: yup.string(),
		startDate: yup.mixed(),
		endDate: yup.mixed().test('end-after-start', 'End date must be after start date', function (endDate) {
			const { startDate } = this.parent || {}
			const startVal = typeof startDate === 'string' ? startDate : ''
			const endVal = typeof endDate === 'string' ? endDate : ''
			if (!startVal || !endVal) {
				return true
			}
			const start = moment(startVal, 'YYYY-MM-DD', true)
			const end = moment(endVal, 'YYYY-MM-DD', true)
			return end.isAfter(start) || end.isSame(start)
		}),
		graduationDate: yup.string(),
		gpa: yup.number(),
		city: yup.string(),
		state: yup.string(),
	})

	const WORK_ITEM_SCHEMA = yup.object().shape({
		company: yup.string(),
		position: yup.string(),
		startDate: yup.mixed(),
		endDate: yup.mixed().test('end-after-start', 'End date must be after start date', function (endDate) {
			const { startDate } = this.parent || {}
			const startVal = typeof startDate === 'string' ? startDate : ''
			const endVal = typeof endDate === 'string' ? endDate : ''
			if (!startVal || !endVal) {
				return true
			}
			const start = moment(startVal, 'YYYY-MM-DD', true)
			const end = moment(endVal, 'YYYY-MM-DD', true)
			return end.isAfter(start) || end.isSame(start)
		}),
		description: yup.string(),
		address1: yup.string(),
		address2: yup.string(),
		city: yup.string(),
		state: yup.string(),
		country: yup.string(),
		zipcode: yup.string(),
	})

	const RESUME_PARSE_SCHEMA = yup.object().shape({
		candidate: CANDIDATE_SCHEMA,
		education: yup.array().of(EDUCATION_ITEM_SCHEMA),
		workHistory: yup.array().of(WORK_ITEM_SCHEMA),
	})

	return {
		PASSWORD_VALIDATION_SCHEMA,
		PASSWORD_CONFIRMATION_VALIDATION_SCHEMA,
		SIGNIN_PASSWORD_VALIDATION_SCHEMA,
		SIGNIN_EMAIL_VALIDATION_SCHEMA,
		NEW_PASSWORD_CONFIRMATION_VALIDATION_SCHEMA,
		NEW_PASSWORD_VALIDATION_SCHEMA,
		// Resume Parse Schemas
		CANDIDATE_SCHEMA,
		EDUCATION_ITEM_SCHEMA,
		WORK_ITEM_SCHEMA,
		RESUME_PARSE_SCHEMA,
	}
}

export default useYupValidation
